import { useState, useEffect } from 'react';
import { Loader2, Trophy, TrendingUp, Users, Gift, Send, Settings, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

export default function AdminLoyalty() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offerDialog, setOfferDialog] = useState(null);
  const [offerDesc, setOfferDesc] = useState('');
  const [offerDiscount, setOfferDiscount] = useState('');
  const [offerFreeItem, setOfferFreeItem] = useState('');
  const [sending, setSending] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [savingTiers, setSavingTiers] = useState(false);

  const fetchData = () => {
    fetch(`${API_URL}/api/admin/loyalty/dashboard`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) setData(d); }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [token]); // eslint-disable-line

  const loadTiers = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/loyalty/tiers`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' });
      if (r.ok) { const d = await r.json(); setTiers(d.tiers); }
    } catch (e) { /* silent */ }
  };

  const saveTiers = async () => {
    setSavingTiers(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/loyalty/tiers`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ tiers })
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      toast.success('Tier settings saved!');
      setShowSettings(false);
      fetchData();
    } catch (e) { toast.error(e.message); }
    finally { setSavingTiers(false); }
  };

  const updateTier = (idx, field, value) => {
    setTiers(prev => prev.map((t, i) => i === idx ? { ...t, [field]: field === 'orders_needed' || field === 'discount_percent' ? parseInt(value) || 0 : value } : t));
  };

  const sendOffer = async () => {
    if (!offerDialog || !offerDesc.trim()) { toast.error('Description required'); return; }
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/loyalty/custom-offer`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: offerDialog.user_id, description: offerDesc, discount_percent: parseInt(offerDiscount) || 0, free_item: offerFreeItem || null })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      setOfferDialog(null);
      setOfferDesc('');
      setOfferDiscount('');
      setOfferFreeItem('');
      fetchData();
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!data) return <p className="text-center text-muted-foreground py-16">Failed to load</p>;

  return (
    <div data-testid="admin-loyalty">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-secondary-900">Loyalty Program</h1>
          <p className="text-sm text-muted-foreground">{data.month} — Discount at each milestone only</p>
        </div>
        {(user?.role === 'super_admin' || user?.role === 'manager') && (
          <Button variant="outline" onClick={() => { loadTiers(); setShowSettings(true); }} data-testid="tier-settings-btn">
            <Settings className="h-4 w-4 mr-2" /> Tier Settings
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center"><Trophy className="h-5 w-5 text-green-600" /></div>
          <div><p className="text-2xl font-bold">{data.eligible_count}</p><p className="text-xs text-muted-foreground">With active discount</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center"><TrendingUp className="h-5 w-5 text-amber-600" /></div>
          <div><p className="text-2xl font-bold">{data.approaching_count}</p><p className="text-xs text-muted-foreground">Approaching first tier</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"><Users className="h-5 w-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold">{data.total_customers}</p><p className="text-xs text-muted-foreground">Active this month</p></div>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Customer Loyalty Tiers</CardTitle><CardDescription>Lifetime orders — sorted by highest</CardDescription></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">Customer</th>
                <th className="text-center text-xs font-medium text-muted-foreground py-3 px-4">Tier</th>
                <th className="text-center text-xs font-medium text-muted-foreground py-3 px-4">Orders</th>
                <th className="text-center text-xs font-medium text-muted-foreground py-3 px-4">Progress</th>
                <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">Discount</th>
                <th className="text-center text-xs font-medium text-muted-foreground py-3 px-4">Action</th>
              </tr></thead>
              <tbody className="divide-y">
                {data.customers.map(c => (
                  <tr key={c.user_id} className="hover:bg-muted/30">
                    <td className="py-3 px-4"><p className="font-medium text-sm">{c.name}</p><p className="text-xs text-muted-foreground">{c.email}</p></td>
                    <td className="py-3 px-4 text-center"><span className="text-lg mr-1">{c.tier_emoji}</span><span className="text-xs font-medium">{c.tier_name}</span></td>
                    <td className="py-3 px-4 text-center"><span className="font-bold text-sm">{c.lifetime_orders}</span></td>
                    <td className="py-3 px-4">
                      <div className="w-full bg-gray-200 rounded-full h-2 mx-auto max-w-[100px]">
                        <div className={`h-2 rounded-full ${c.current_tier >= 4 ? 'bg-amber-500' : c.current_tier > 0 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${c.progress_percent}%` }} />
                      </div>
                      <p className="text-[10px] text-muted-foreground text-center mt-0.5">{c.orders_to_next > 0 ? `${c.orders_to_next} to next` : 'Max'}</p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {c.discount_percent > 0 ? <Badge className="bg-green-100 text-green-800 text-xs">{c.discount_percent}%</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOfferDialog(c)} data-testid={`offer-${c.user_id}`}>
                        <Gift className="h-3 w-3 mr-1" /> Offer
                      </Button>
                    </td>
                  </tr>
                ))}
                {data.customers.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">No orders this month</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Custom Offer Dialog */}
      <Dialog open={!!offerDialog} onOpenChange={open => { if (!open) setOfferDialog(null); }}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /> Send Custom Offer</DialogTitle></DialogHeader>
          {offerDialog && (
            <div className="space-y-4 pt-2">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium text-sm">{offerDialog.tier_emoji} {offerDialog.name}</p>
                <p className="text-xs text-muted-foreground">{offerDialog.email} · {offerDialog.lifetime_orders} lifetime orders</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Offer Description *</label>
                <Input value={offerDesc} onChange={e => setOfferDesc(e.target.value)} placeholder="e.g. Free Coke with your next order!" data-testid="offer-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Discount %</label>
                  <Input type="number" value={offerDiscount} onChange={e => setOfferDiscount(e.target.value)} placeholder="0" min="0" max="50" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Free Item</label>
                  <Select value={offerFreeItem} onValueChange={setOfferFreeItem}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="drink">Free Drink</SelectItem>
                      <SelectItem value="side">Free Side</SelectItem>
                      <SelectItem value="meal">Free Meal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOfferDialog(null)}>Cancel</Button>
            <Button onClick={sendOffer} disabled={sending || !offerDesc.trim()} data-testid="send-offer">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Send Offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tier Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Loyalty Tier Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs text-muted-foreground">Customise tier names, order thresholds, discounts and free items. Discount applies <strong>once</strong> when the customer reaches the milestone, then resets.</p>
            {tiers.map((t, idx) => (
              <div key={t.tier || idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-muted/30 rounded-lg">
                <div className="col-span-1">
                  <Label className="text-[10px]">Emoji</Label>
                  <Input value={t.emoji || ''} onChange={e => updateTier(idx, 'emoji', e.target.value)} className="text-center text-lg h-9 px-1" />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px]">Name</Label>
                  <Input value={t.name || ''} onChange={e => updateTier(idx, 'name', e.target.value)} className="h-9 text-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px]">Orders</Label>
                  <Input type="number" value={t.orders_needed || ''} onChange={e => updateTier(idx, 'orders_needed', e.target.value)} className="h-9 text-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px]">Discount %</Label>
                  <Input type="number" value={t.discount_percent || ''} onChange={e => updateTier(idx, 'discount_percent', e.target.value)} className="h-9 text-sm" />
                </div>
                <div className="col-span-4">
                  <Label className="text-[10px]">Free Item</Label>
                  <Select value={t.free_item || 'none'} onValueChange={v => updateTier(idx, 'free_item', v === 'none' ? null : v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="drink">Drink</SelectItem>
                      <SelectItem value="side">Side</SelectItem>
                      <SelectItem value="side_and_drink">Side + Drink</SelectItem>
                      <SelectItem value="meal">Meal</SelectItem>
                      <SelectItem value="everything">Everything</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button onClick={saveTiers} disabled={savingTiers} data-testid="save-tiers">
              {savingTiers ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Tiers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
